import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plug, AlertCircle, Loader, Lock } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuthStore } from '../store/auth';
import { api } from '../lib/api/services';
import type { InviteValidationResponse } from '../types';

export const AdminSignup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [invite, setInvite] = useState<InviteValidationResponse['invite']>();
  const [error, setError] = useState<string>();
  const setUser = useAuthStore((state) => state.setUser);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setIsValidating(false);
      return;
    }

    api.validateInvite(token)
      .then((response) => {
        if (response.success && response.invite) {
          setInvite(response.invite);
          setFormData(prev => ({ ...prev, email: response.invite.email }));
        } else {
          setError(response.message || 'Invalid invite');
        }
      })
      .catch((error) => {
        setError(error instanceof Error ? error.message : 'Failed to validate invite');
      })
      .finally(() => {
        setIsValidating(false);
      });
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !invite) {
      toast.error('Invalid invite link');
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.signupAdmin({
        ...formData,
        token,
      });

      if (response.success) {
        localStorage.setItem('token', response.token);
        setUser(response.user);
        toast.success('Welcome to GoGlobe Admin Portal!');
        navigate('/');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md text-center p-8">
          <Lock className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold mb-2">Admin Access Only</h1>
          <p className="text-gray-400 mb-6">
            This area is restricted to invited administrators only. You need a valid invite link to create an admin account.
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md text-center p-8">
          <Loader className="w-12 h-12 text-cyan-400 mx-auto mb-4 animate-spin" />
          <h1 className="text-2xl font-semibold mb-2">Validating Invite</h1>
          <p className="text-gray-400">Please wait while we validate your invite link...</p>
        </Card>
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold mb-2">Invalid Invite Link</h1>
          <p className="text-gray-400 mb-6">
            {error || 'The admin invite link appears to be invalid or has expired.'}
            Please contact support for assistance.
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <Plug className="w-12 h-12 text-cyan-400 mb-4" />
          <h1 className="text-2xl font-semibold">Create Admin Account</h1>
          <p className="text-gray-400 mt-2">Complete your account setup</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              disabled
              className="w-full bg-black/40 border border-cyan-500/30 rounded-md px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Password
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              className="w-full bg-black/40 border border-cyan-500/30 rounded-md px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="Create a secure password"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              First Name
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              className="w-full bg-black/40 border border-cyan-500/30 rounded-md px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="Enter your first name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Last Name
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              className="w-full bg-black/40 border border-cyan-500/30 rounded-md px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="Enter your last name"
              required
            />
          </div>

          <Button type="submit" className="w-full" isLoading={isLoading}>
            Create Account
          </Button>
        </form>
      </Card>
    </div>
  );
};